import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import './Contact.css';

function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '', // Bot trap
  });
  const [status, setStatus] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  // Prepopulate form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check honeypot (hidden field - if filled, it's a bot)
    if (formData.honeypot) {
      console.log('Bot detected');
      return;
    }

    // Check reCAPTCHA for non-authenticated users
    if (!user && !recaptchaToken) {
      setStatus('error');
      return;
    }

    setStatus('sending');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${apiUrl}/api/auth/contact`, {
        ...formData,
        recaptchaToken: user ? null : recaptchaToken, // Only send token if not logged in
      });

      setStatus('success');
      // Only clear subject and message (keep name/email if logged in)
      setFormData((prev) => ({
        ...prev,
        subject: '',
        message: '',
        // Reset name/email only if not logged in
        ...(user ? {} : { name: '', email: '' }),
      }));

      setTimeout(() => navigate(-1), 3000);
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="brand-section">
          <img src="/PMFullLogo.png" alt="Playmaker Media Kit" />
        </div>

        <div className="auth-header">
          <h3>Contact Us</h3>
          <p>Have questions? We're here to help.</p>
        </div>

        {status === 'success' && (
          <div className="success-message">
            ✅ Message sent! We'll respond within 24 hours.
          </div>
        )}

        {status === 'error' && (
          <div className="error-message">
            Failed to send. Please email us directly at
            contact@playmakermediakit.com
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              required
              disabled={status === 'sending'}
              readOnly={!!user}
              style={
                user
                  ? { backgroundColor: '#f7fafc', cursor: 'not-allowed' }
                  : {}
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              disabled={status === 'sending'}
              readOnly={!!user}
              style={
                user
                  ? { backgroundColor: '#f7fafc', cursor: 'not-allowed' }
                  : {}
              }
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              id="subject"
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Privacy request, account question, etc."
              required
              disabled={status === 'sending'}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              placeholder="How can we help you?"
              required
              disabled={status === 'sending'}
            />
          </div>

          {/* Honeypot field - hidden from users, catches bots */}
          <input
            type="text"
            name="honeypot"
            value={formData.honeypot}
            onChange={handleChange}
            style={{ display: 'none' }}
            tabIndex="-1"
            autoComplete="off"
          />

          {/* reCAPTCHA - only show for non-authenticated users */}
          {!user && (
            <div className="recaptcha-container">
              <ReCAPTCHA
                sitekey="6LdzIE4sAAAAAHfMvWRowrzhGfxuXn3GwDGWlacY"
                onChange={(token) => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={status === 'sending'}
          >
            {status === 'sending' ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            <button
              onClick={() => navigate(-1)}
              className="btn-link"
              style={{
                background: 'none',
                border: 'none',
                color: '#48bb78',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit',
              }}
            >
              ← Back
            </button>
          </p>
          <p className="contact-footer-info">
            <strong>Direct Email:</strong> contact@playmakermediakit.com
          </p>
          <p className="contact-footer-note">
            We typically respond within 24 hours
          </p>
          <p>
            <a href="/privacy">Privacy Policy</a>
            {/* <span> • </span>
            <a href="/terms">Terms of Service</a> */}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Contact;
